import { chunk, sum, uniq, uniqWith } from 'es-toolkit';
import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';

import { getLumberDescription } from '@paragon/contract-library';
import { distanceEquals, Polygon2D, Vector2D } from '@paragon/geometry-library';

import { MemberCuts, MembersOfCutAndLength } from '../../../../store/selectors/assemblyPartsUtilities';
import { getColorForMemberSelector } from '../../../../store/selectors/getColorFromMemberDefinitionSelector';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../../store/selectors/getSelectedItemSelector';
import { useAppSelector } from '../../../hooks';

interface AssemblyPartDiagramProps {
  readonly getStandardizedMemberPolygonAtOrigin: (
    standardizedMemberWidth: number,
    standardizeMemberLength: number,
  ) => Polygon2D;
  readonly memberCuts: MemberCuts;
  readonly membersOfCutAndLength: MembersOfCutAndLength[];
  readonly useColoring?: boolean;
}

const diagramWidth = 2000;
const diagramHeight = 400;
const xSpacingForAngleText = diagramWidth / 10;
const standardizedMemberWidth = diagramHeight;
const standardizedMemberLength = diagramWidth - xSpacingForAngleText * 2;
const memberPolygonTranslation = new Vector2D(xSpacingForAngleText, 0);
const fontPointsForStandardizedMemberWidth = diagramHeight / 2;

export function AssemblyMemberPartDiagram(props: AssemblyPartDiagramProps) {
  const { getStandardizedMemberPolygonAtOrigin, memberCuts, membersOfCutAndLength, useColoring } = props;
  const {
    lumber,
    leftCutAnglesBottomToTop,
    leftCutDistancesBottomToTopInInches,
    rightCutAnglesBottomToTop,
    rightCutDistancesBottomToTopInInches,
  } = memberCuts;
  const selectedItem = useAppSelector(getSingleSelectedItemEntryOrNull);
  const memberColorGetter = useAppSelector(getColorForMemberSelector);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const members = useMemo(
    () =>
      membersOfCutAndLength.flatMap((membersOfCutAndLengthEntry) =>
        membersOfCutAndLengthEntry.componentInfo.flatMap((componentInfo) => componentInfo.members),
      ),
    [membersOfCutAndLength],
  );
  const memberGuids = useMemo(() => members.map((member) => member.guid), [members]);
  const selectedItemStyling = useMemo(
    () =>
      selectedItem === null || selectedItem.type !== 'member'
        ? {}
        : memberGuids.includes(selectedItem.item.guid)
          ? { border: '1px white solid' }
          : {},
    [selectedItem, memberGuids],
  );
  const memberColor = useMemo(() => {
    if (!useColoring) return 'orange';
    const colorsForMembers = uniq(members.map(memberColorGetter));
    return colorsForMembers.length === 1 ? `#${colorsForMembers[0].getHexString()}` : 'orange';
  }, [useColoring, members, memberColorGetter]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const shouldShowLeftCutDistances = useMemo(
    () => uniqWith(memberCuts.leftCutDistancesBottomToTopInInches, distanceEquals).length > 1,
    [memberCuts],
  );
  const shouldShowRightCutDistances = useMemo(
    () => uniqWith(memberCuts.rightCutDistancesBottomToTopInInches, distanceEquals).length > 1,
    [memberCuts],
  );
  const standardizedMemberPolygonAtOriginWithMargin = useMemo(() => {
    const standardizedMemberPolygonAtOrigin = getStandardizedMemberPolygonAtOrigin(
      standardizedMemberWidth,
      standardizedMemberLength,
    );
    return new Polygon2D(
      standardizedMemberPolygonAtOrigin.vertices.map((vertex) => vertex.translate(memberPolygonTranslation)),
    );
  }, [getStandardizedMemberPolygonAtOrigin]);

  const quantitiesAndLengths = useMemo(
    () =>
      membersOfCutAndLength.map(
        (membersOfCutAndLengthInfo) =>
          `(${sum(
            membersOfCutAndLengthInfo.componentInfo.map((componentInfo) => componentInfo.quantity),
          )}) ${distanceFormatter(membersOfCutAndLengthInfo.length)}`,
      ),
    [membersOfCutAndLength, distanceFormatter],
  );
  const lumberDescription = useMemo(() => getLumberDescription(lumber), [lumber]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) return;

    // Set up canvas
    canvasContext.setTransform(1, 0, 0, 1, 0, 0);
    canvasContext.clearRect(0, 0, canvas.width, canvas.height);
    canvasContext.translate(0, diagramHeight);
    canvasContext.scale(1, -1);

    // Draw standardized member polygon
    canvasContext.fillStyle = memberColor;
    const memberVertices = standardizedMemberPolygonAtOriginWithMargin.vertices;
    canvasContext.beginPath();
    canvasContext.moveTo(memberVertices[0].x, memberVertices[0].y);
    for (let i = 1; i < memberVertices.length; i++) {
      canvasContext.lineTo(memberVertices[i].x, memberVertices[i].y);
    }
    canvasContext.fill();

    // Draw text
    canvasContext.fillStyle = 'black';

    // At least two lines to start: One for quantities and lengths, one for lumber description
    let linesNeeded = 2;
    canvasContext.font = `${fontPointsForStandardizedMemberWidth / linesNeeded}px sans-serif`;

    // Build the lines of text for display
    // Determine if any lines will be too long for display and therefore should be split across multiple lines
    const assemblyPartDescriptionsWrapped: string[] = [];
    const proposedQuantitiesAndLengthsLine = quantitiesAndLengths.join(', ');
    const quantityAndLengthLineLength = canvasContext.measureText(proposedQuantitiesAndLengthsLine).width;
    let quantityAndLengthLineLengths = [quantityAndLengthLineLength];
    if (quantityAndLengthLineLength > standardizedMemberLength) {
      // For simplicity, this algorithm assumes that each quantity and length description is less than the length of
      // the standardized member. If this is not the case, you should re-configure the diagram size or consider an
      // alternate method of displaying this information than on top of the member.

      let quantitiesAndLengthsChunks: string[][] = [];
      while (quantityAndLengthLineLengths.some((lineLength) => lineLength > standardizedMemberLength)) {
        linesNeeded += 1;
        canvasContext.font = `${fontPointsForStandardizedMemberWidth / linesNeeded}px sans-serif`;

        // - 1 because one line is for the lumber description
        const quantityAndLengthChunkSize = Math.ceil(quantitiesAndLengths.length / (linesNeeded - 1));
        quantitiesAndLengthsChunks = chunk(quantitiesAndLengths, quantityAndLengthChunkSize);
        quantityAndLengthLineLengths = quantitiesAndLengthsChunks.map(
          (quantitiesAndLengthsChunk) => canvasContext.measureText(quantitiesAndLengthsChunk.join(', ')).width,
        );
      }
      assemblyPartDescriptionsWrapped.push(
        ...quantitiesAndLengthsChunks.map((quantitiesAndLengthsChunk) => quantitiesAndLengthsChunk.join(', ')),
      );
    } else assemblyPartDescriptionsWrapped.push(proposedQuantitiesAndLengthsLine);
    // It is possible this line is overlength, but it will require a different algorithm for that. If this line being
    // overlength is an issue, we can implement that.
    assemblyPartDescriptionsWrapped.push(lumberDescription);

    canvasContext.scale(1, -1); // Invert the canvas and the y values below so the text is not upside down
    for (let i = 0; i < assemblyPartDescriptionsWrapped.length; i++) {
      const descriptionLine = assemblyPartDescriptionsWrapped[i];
      const descriptionLineLength = canvasContext.measureText(descriptionLine).width;
      const canvasX = canvas.width / 2 - descriptionLineLength / 2;
      // Lines are placed evenly spaced. For example, if there are two lines, they are placed at 1/3 and 2/3 of the
      // height. Some funny things happen due to the coordinate system of the canvas, like using the negative sign and
      // doing 2/3 before 1/3 so that the text is in the top to bottom order we would expect.
      const relativePositionNumerator = i + 1;
      const relativePositionDenominator = assemblyPartDescriptionsWrapped.length + 1;
      const invertedRelativePosition = 1 - relativePositionNumerator / relativePositionDenominator;
      const canvasY = -canvas.height * invertedRelativePosition;
      canvasContext.fillText(descriptionLine, canvasX, canvasY);
    }

    // Build the degrees labels and cut distances; assume at least one and no more than 3 per side
    canvasContext.fillStyle = 'white';
    canvasContext.font = `${fontPointsForStandardizedMemberWidth / 3}px sans-serif`;
    const sampleTextHeightMeasurement = canvasContext.measureText('90°'); // 90° is only used as a sample
    const fontHeight =
      sampleTextHeightMeasurement.fontBoundingBoxDescent + sampleTextHeightMeasurement.fontBoundingBoxAscent;

    const leftCutAnglesBottomToTopRounded = leftCutAnglesBottomToTop.map((angle) => Math.round(angle * 10) / 10);
    const leftCutAnglesAvailableMargin = standardizedMemberWidth - leftCutAnglesBottomToTopRounded.length * fontHeight;
    const leftCutAnglesSpacing = leftCutAnglesAvailableMargin / (leftCutAnglesBottomToTopRounded.length + 1);

    for (let i = 0; i < leftCutAnglesBottomToTopRounded.length; i += 1) {
      const leftCutDistanceAndAngle = `${
        shouldShowLeftCutDistances ? `${distanceFormatter(leftCutDistancesBottomToTopInInches[i])} ` : ''
      }${leftCutAnglesBottomToTopRounded[i]}°`;
      canvasContext.fillText(leftCutDistanceAndAngle, 0, -(leftCutAnglesSpacing * (i + 1) + i * fontHeight));
    }

    const rightCutAnglesBottomToTopRounded = rightCutAnglesBottomToTop.map((angle) => Math.round(angle * 10) / 10);
    const rightCutAnglesAvailableMargin =
      standardizedMemberWidth - rightCutAnglesBottomToTopRounded.length * fontHeight;
    const rightCutAnglesSpacing = rightCutAnglesAvailableMargin / (rightCutAnglesBottomToTopRounded.length + 1);

    for (let i = 0; i < rightCutAnglesBottomToTopRounded.length; i += 1) {
      const rightCutDistanceAndAngle = `${rightCutAnglesBottomToTopRounded[i]}°${
        shouldShowRightCutDistances ? ` ${distanceFormatter(rightCutDistancesBottomToTopInInches[i])}` : ''
      }`;
      const angleStringWidth = canvasContext.measureText(rightCutDistanceAndAngle).width;
      canvasContext.fillText(
        rightCutDistanceAndAngle,
        diagramWidth - angleStringWidth,
        -(rightCutAnglesSpacing * (i + 1) + i * fontHeight),
      );
    }

    canvasContext.scale(1, -1);
  }, [
    standardizedMemberPolygonAtOriginWithMargin,
    quantitiesAndLengths,
    lumberDescription,
    memberColor,
    leftCutAnglesBottomToTop,
    rightCutAnglesBottomToTop,
    leftCutDistancesBottomToTopInInches,
    rightCutDistancesBottomToTopInInches,
    shouldShowLeftCutDistances,
    shouldShowRightCutDistances,
    distanceFormatter,
  ]);

  return (
    <Box sx={{ ...selectedItemStyling, m: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <canvas ref={canvasRef} width={diagramWidth} height={diagramHeight} />
    </Box>
  );
}
